import { useSyncMachineWithArtBlocksClient } from '@/hooks/use-sync-machine-with-artblocks-client'
import { excessSettlementFundsClaimMachine } from '@artblocks/sdk/dist/machines/excess-settlement-funds-claim-machine'
import { ActorRefFrom } from '@artblocks/sdk/dist/machines/utils'
import { useArtBlocksClient } from '@components/artblocks-provider'
import { useSelector } from '@xstate/react'
import { useToast } from 'design-configuration/components/toast'
import { useEffect } from 'react'
import { ExcessSettlementFundsManagerMachineContext } from '.'
import { useExcessSettlementFundsClaimMachineRefs } from './hooks'
import { formatEther } from 'viem'

const RECEIPT_POLLING_INTERVAL = 60 * 60 * 24 * 7 * 1000 // 1 week

export function ExcessSettlementFundsManagerMachineContextProvider({ children }: { children: React.ReactNode }) {
  const artblocksClient = useArtBlocksClient()

  return (
    <ExcessSettlementFundsManagerMachineContext.Provider
      options={{ input: { artblocksClient, receiptPollingInterval: RECEIPT_POLLING_INTERVAL } }}
    >
      <ExcessSettlementFundsManagerMachineManager />
      {children}
    </ExcessSettlementFundsManagerMachineContext.Provider>
  )
}

function ExcessSettlementFundsManagerMachineManager() {
  const managerMachine = ExcessSettlementFundsManagerMachineContext.useActorRef()
  useSyncMachineWithArtBlocksClient(managerMachine)

  const excessSettlementFundClaimMachineRefs = useExcessSettlementFundsClaimMachineRefs()
  const claimMachineEntries = [...(excessSettlementFundClaimMachineRefs?.entries() ?? [])]

  return (
    <>
      {claimMachineEntries.map(([claimId, claimMachineRef]) => (
        <ExcessSettlementFundsClaimNotifier key={claimId} claimMachineRef={claimMachineRef} />
      ))}
    </>
  )
}

function ExcessSettlementFundsClaimNotifier({
  claimMachineRef
}: {
  claimMachineRef: ActorRefFrom<typeof excessSettlementFundsClaimMachine>
}) {
  const { toast } = useToast()

  const claimState = useSelector(
    claimMachineRef,
    (state) => state,
    (a, b) => {
      return a.value === b.value
    }
  )

  const isErrored = claimState.matches('error')
  useEffect(() => {
    if (isErrored) {
      toast({
        title: 'Error claiming funds',
        description: 'Please try again',
        variant: 'destructive'
      })
    }
  }, [isErrored, toast])

  const isSuccessful = claimState.matches('success')
  useEffect(() => {
    if (isSuccessful) {
      const amount = claimState.context.receipt.excess_settlement_funds
      const amountInEth = amount ? Number(formatEther(BigInt(amount))).toFixed(4) : '--'
      const projectName = claimState.context.receipt.project.name
      toast({
        title: 'Settlement claimed successfully',
        description: (
          <>
            Your settlement of {amountInEth} ETH from <span className="italic">{projectName}</span> has been sent to
            your wallet.
          </>
        ),
        variant: 'default'
      })
    }
  }, [isSuccessful, toast, claimState.context.receipt.project.name, claimState.context.receipt.excess_settlement_funds])

  return null
}
